import { AppIcon } from "~/components/app-icon";
import type { WorkbenchArtifact } from "~/lib/constants/workbench";
import * as m from "~/paraglide/messages.js";

/** WhatsApp thread: the customer's question, then the prepared reply. */
const ChatThread = () => (
  <div className="conversation">
    <div className="incoming">
      <p>{m.wb_support_incoming()}</p>
      <span>{m.wb_support_time()}</span>
    </div>
    <p className="draft-label">{m.wb_support_draft_label()}</p>
    <div className="outgoing">
      <p>{m.wb_support_reply_1()}</p>
      <p>{m.wb_support_reply_2()}</p>
      <span>{m.wb_support_reply_status()}</span>
    </div>
  </div>
);

const EmailDraft = () => (
  <div className="email-art">
    <div className="email-head">
      <strong>{m.wb_sales_head()}</strong>
      <span>{m.wb_sales_status()}</span>
    </div>
    <div className="email-address">
      {m.wb_sales_to()} <br />
      {m.wb_sales_subject()}
    </div>
    <div className="email-body">
      <p>{m.wb_sales_body_1()}</p>
      <p>{m.wb_sales_body_2()}</p>
      <p>{m.wb_sales_body_3()}</p>
      <p>{m.wb_sales_body_4()}</p>
      <p>
        {m.wb_sales_body_5()} <br />
        {m.wb_sales_body_6()}
      </p>
    </div>
  </div>
);

const OrderRecord = () => (
  <div className="order-art">
    <div className="order-head">
      <strong>{m.wb_operations_head()}</strong>
      <span>{m.wb_operations_status()}</span>
    </div>
    <table aria-label={m.wb_operations_table_label()}>
      <tbody>
        <tr>
          <td>{m.wb_operations_row_customer()}</td>
          <td>{m.wb_operations_val_customer()}</td>
        </tr>
        <tr>
          <td>{m.wb_operations_row_item()}</td>
          <td>{m.wb_operations_val_item()}</td>
        </tr>
        <tr>
          <td>{m.wb_operations_row_qty()}</td>
          <td>{m.wb_operations_val_qty()}</td>
        </tr>
        <tr>
          <td>{m.wb_operations_row_delivery()}</td>
          <td>{m.wb_operations_val_delivery()}</td>
        </tr>
        <tr>
          <td>{m.wb_operations_row_address()}</td>
          <td>{m.wb_operations_val_address()}</td>
        </tr>
      </tbody>
    </table>
    <p className="order-note">{m.wb_operations_note()}</p>
  </div>
);

const ReportDraft = () => (
  <div className="report-art">
    <p className="report-date">{m.wb_marketing_date()}</p>
    <h4>{m.wb_marketing_heading()}</h4>
    <p>{m.wb_marketing_body()}</p>
    <div className="report-emphasis">
      <strong>{m.wb_marketing_emphasis_title()}</strong>
      <p>{m.wb_marketing_emphasis_body()}</p>
    </div>
    <p>
      <strong>{m.wb_marketing_next_title()}</strong>
      <br />
      {m.wb_marketing_next_body()}
    </p>
  </div>
);

const TeamBrief = () => (
  <div className="team-art">
    <div className="team-channel">{m.wb_management_channel_name()}</div>
    <div className="team-head">
      <AppIcon name="slack" />
      <span>
        <strong>{m.wb_management_head_title()}</strong>
        <br />
        {m.wb_management_head_meta()}
      </span>
    </div>
    <p>{m.wb_management_intro()}</p>
    <ul>
      <li>
        <strong>{m.wb_management_item_1_label()}</strong>{" "}
        {m.wb_management_item_1_body()}
      </li>
      <li>
        <strong>{m.wb_management_item_2_label()}</strong>{" "}
        {m.wb_management_item_2_body()}
      </li>
      <li>
        <strong>{m.wb_management_item_3_label()}</strong>{" "}
        {m.wb_management_item_3_body()}
      </li>
    </ul>
    <p>{m.wb_management_outro()}</p>
  </div>
);

export function Artifact({
  artifact,
}: Readonly<{ artifact: WorkbenchArtifact }>) {
  switch (artifact.kind) {
    case "chat":
      return <ChatThread />;
    case "email":
      return <EmailDraft />;
    case "order":
      return <OrderRecord />;
    case "report":
      return <ReportDraft />;
    case "brief":
      return <TeamBrief />;
    default: {
      // The exhaustiveness `workbench.ts` promises. A plain `return null` here
      // would satisfy the compiler for a sixth kind and render nothing at
      // runtime, which is exactly what the discriminated union exists to stop.
      const unhandled: never = artifact;
      return unhandled;
    }
  }
}
